/**
 * The action catalogue (spec 6.1, IP-46): every Console write is one of these
 * types, and each names
 *
 *   actionClass    L1, L2 or L2+T - fixed in code; no configuration lowers it
 *   claims         the claims that grant it (any one of them)
 *   params(body)   the normaliser: validates the request body and returns the
 *                  exact parameters the step-up code is bound to and the
 *                  action then runs with - ids lower-cased, id lists sorted,
 *                  decimals at their column's scale, text trimmed. It refuses a
 *                  missing or malformed field with DomainError('VALIDATION', 400)
 *                  naming the field, drops fields it does not know (stepUp,
 *                  anything else), and is idempotent: params(params(b)) is
 *                  params(b), which authorise() relies on to refuse a router
 *                  that passes anything but its output.
 *   group, entityKey, leg
 *                  the step_up_evidence approval group: 'entity' - the id of
 *                  the entity the action is a leg of (entityKey names the
 *                  parameter, or, for the action that creates the entity,
 *                  INTAKE_UPLOAD's batch, MATCH_CLAIM's match and
 *                  HOLDER_PROPOSE's request, the new row's id), so unique
 *                  (approval_group, uid) holds its legs to different people;
 *                  or 'new' - a fresh uuid per action. leg is approval_index.
 *                  ORDER_REVIEW and ORDER_APPROVE each take a new group
 *                  (IP-62: the approval batch); the order row's own CHECK
 *                  holds approver <> the latest reviewer, and 0018's trigger
 *                  order_approver_is_not_a_reviewer holds the approver to
 *                  none of the order's reviewers, of any cycle (IP-128).
 *   selfChangeKey  (access actions) the parameter that must never be the admin
 *                  themself (IP-50): their uid, their email, a role they hold.
 *   stepUp         (IP-76) true when the action needs a fresh authenticator
 *                  code, bound to its params (authorise.js); false when the
 *                  person's MFA session is authority enough (authoriseSession).
 *                  Today the three intake actions (IP-76, IP-96): the upload,
 *                  and the accept and reject that decide what a file sets
 *                  off; the three fund-fee actions (IP-101); and (IP-111,
 *                  D-D) every pricing and cash decision - PRICE_OVERRIDE,
 *                  PRICE_APPROVE, PRICE_REJECT, MATCH_CLAIM,
 *                  MATCH_APPROVE, MATCH_REJECT, LINE_CLEAR, LINE_RETURN and
 *                  RECEIPT_REFUND. IP-199 (gap G; owner, 19 Oct 2026):
 *                  approving or rejecting a transaction needs a code too -
 *                  ORDER_APPROVE, ORDER_REJECT and ORDER_REJECT_HOLD - which
 *                  reverses IP-76 for transactions. Payouts and holder
 *                  approvals stay on the session. Either way the action writes its
 *                  step_up_evidence row, so four-eyes holds the same.
 *
 * (IP-67..71) LINE_CLEAR carries the bank's clearedOn and bankReference;
 * LINE_RETURN and RECEIPT_REFUND are new. Migration 0007 (Task 6) adds the
 * two new types to step_up_evidence's catalogued-action CHECK - and HOLDER_REJECT
 * (Ruling R16, Task 8 review), a holder proposal declined or withdrawn.
 *
 * (IP-78) The access actions follow the Core's own user pool: USER_INVITE
 * carries the person's names; USER_RESEND_INVITE, USER_REINSTATE,
 * USER_ROLES_SET and MFA_RESET are new; CREDENTIAL_ACTIVATE and
 * CREDENTIAL_REVOKE are gone - an authenticator is active the moment Cognito
 * and the Core copy both accept its first code (IP-74), and an admin resets it
 * with MFA_RESET. Migration 0008 catalogues the new types.
 *
 * (IP-85, IP-86) LIV no longer approves (IP-82), so the Core's review and
 * approval are the only ones, and they gain what LIV's confirmation step did:
 * the checker may send a reviewed order back (ORDER_SEND_BACK) or reject one
 * (ORDER_REJECT). Both are core.order.approve's, run on the MFA session, and
 * write one evidence row of their own (leg 1 of a fresh group: nothing is
 * paired with them). Migration 0010 catalogues the two new types.
 *
 * (IP-127, IP-130, IP-136) The review is mandatory and may be made by several
 * people: ORDER_REVIEW carries one comment for every order it names (3 to
 * 1,000 characters; line breaks and tabs allowed) and the state_version each
 * order showed the reviewer (expectedVersions, as ORDER_APPROVE's), and no fee
 * - IP-85's fee edit at review is gone (fees are percentages of the fund's
 * rate, IP-136). ORDER_REJECT takes an order in any open state (IP-130).
 *
 * (IP-98..IP-102) A fund's fees change by four eyes, each leg with a fresh
 * code: FUND_FEES_PROPOSE (core.fees.propose) names the exit fee (NONE or
 * PERCENT since IP-136, and the holding period in months), the entry fee
 * (PERCENT) and the effective date; FUND_FEES_APPROVE and
 * FUND_FEES_REJECT (core.fees.approve) decide it. Both decisions are leg 2 of
 * the proposal's group, so unique (approval_group, uid) refuses the proposer
 * either. Migration 0011 catalogues the three types and the two claims.
 *
 * (IP-103) INTAKE_UPLOAD takes six classes: NAV_VALUES and BANK_STATEMENT
 * with no loadKind, as before, and CALENDAR, UNIT_PRICES, HOLDINGS and
 * TRANSACTIONS each with exactly one of INITIAL and DAILY (intake/kinds.js;
 * migration 0012's intake_batch_kind_for_core_data) - and since 0026 the WM
 * files, WM_HOLDINGS and WM_TRANSACTIONS, the same way. The extra claims a kind
 * needs - core.intake.initial, and the dealing claims for daily transactions -
 * are judged by intake/upload.js against intake/registry.js, not here: the
 * catalogue names the one claim every upload needs.
 *
 * INTAKE_UPLOAD_BATCH (core.intake.upload) is several uploads on one code:
 * its params are { files } - each file's facts exactly as INTAKE_UPLOAD's,
 * 2 to MAX_UPLOAD_FILES of them, in order - so the one code is bound to every
 * file's SHA-256. step_up_evidence's challenge_id is unique, so the ticket
 * writes ONE evidence row (leg 1 of a fresh group) and every batch it opens
 * names it; each batch's own CHECK (intake_batch_uploader_is_not_acceptor)
 * holds its acceptor to another person. Migration 0013 catalogues the type.
 *
 * (IP-111, IP-112) The ten pricing and cash decisions need a fresh code
 * (D-D); migration 0012 holds a NAV run's approval and verification and a
 * match's claim and decision to one in the database as well. LINE_NOTE is
 * new: a note on a statement line, by either cash claim, on the session - a
 * note is not a decision. Migration 0012 catalogues it.
 *
 * (IP-135) DAY_ADVANCE (core.book.close, on the session) moves a business
 * day on, and may force it past a missing price, or statements and the
 * funding window, with a reason (business-day/action.js). Migration 0019
 * catalogues it.
 */
import { DomainError } from '../errors.js';
import { cmp, toFixed } from '../decimal.js';
import { FILE_CLASSES, LOAD_KINDS, takesLoadKind } from '../intake/kinds.js';
import { isCalendarDate } from '../orders/dealing-date.js';
import { coveredBy, isNotification, notificationOf } from '../notifications/catalogue.js';
import { isClaim } from './claims.js';

// ---------------------------------------------------------------------------
// Field validators. Each takes (body, key) and returns the normalised value or
// throws VALIDATION naming the key.
// ---------------------------------------------------------------------------

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX64 = /^[0-9a-f]{64}$/i;
const ROLE = /^[A-Z][A-Z0-9_]{1,39}$/;
const FUND = /^UT[A-Z]{2,7}$/;
const CODE_NAME = /^[A-Z][A-Z0-9_]{1,39}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// The C0 controls and DEL, NEL, and the Unicode line and paragraph separators:
// a one-line (plain) value must stay one line wherever it travels - a JSON
// Schema '^.+$' (the Core's events' reasons[].message) does not match
// U+2028 or U+2029 any more than \n.
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f\u0085\u2028\u2029]/;

/** Upload cap (IP-44): 4 MB decoded. */
export const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;
/** An approval request carries at most 200 orders (IP-62). */
export const MAX_ORDERS = 200;

function invalid(key, message) {
  return new DomainError('VALIDATION', key + ': ' + message, { status: 400, details: { field: key } });
}

function present(body, key) {
  const value = body[key];
  if (value === undefined || value === null) throw invalid(key, 'is required');
  return value;
}

function uuid(body, key) {
  const value = present(body, key);
  if (typeof value !== 'string' || !UUID.test(value)) throw invalid(key, 'must be a uuid');
  return value.toLowerCase();
}

function hex64(body, key) {
  const value = present(body, key);
  if (typeof value !== 'string' || !HEX64.test(value)) throw invalid(key, 'must be 64 hexadecimal characters');
  return value.toLowerCase();
}

function date(body, key) {
  const value = present(body, key);
  if (!isCalendarDate(value)) throw invalid(key, 'must be a real YYYY-MM-DD date');
  return value;
}

/** Trimmed text of min..max characters; identifiers (plain) refuse control characters. */
function text(body, key, { min = 1, max, plain = false }) {
  const value = present(body, key);
  if (typeof value !== 'string') throw invalid(key, 'must be text');
  const trimmed = value.trim();
  if (trimmed.length < min) throw invalid(key, min === 1 ? 'must not be empty' : 'must be at least ' + min + ' characters');
  if (trimmed.length > max) throw invalid(key, 'must be at most ' + max + ' characters');
  if (plain && CONTROL.test(trimmed)) throw invalid(key, 'must not contain control characters');
  return trimmed;
}

function optionalText(body, key, options) {
  const value = body[key];
  if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) return null;
  return text(body, key, options);
}

/** true or false; absent or null is false, and anything but a boolean is refused (never "yes" read as true). */
function optionalFlag(body, key) {
  const value = body[key];
  if (value === undefined || value === null) return false;
  if (typeof value !== 'boolean') throw invalid(key, 'must be true or false');
  return value;
}

/** true or false, and required: absent is refused, never read as either. */
function flag(body, key) {
  const value = present(body, key);
  if (typeof value !== 'boolean') throw invalid(key, 'must be true or false');
  return value;
}

/**
 * IP-199 (gap H; owner, 19 Oct 2026): every reason a person types follows the
 * Console's own rule (reasonProblem in apps/console-mfe approval/orders.js and
 * businessDay.js: REASON_MIN 3, REASON_MAX 500, one line): at least 3
 * characters after trimming, at most 500, and no line breaks, tabs or other
 * control characters. Before this, most refusals here took a single character,
 * so a direct API call could leave an audit trail reading "x". The server now
 * refuses what the Console's form would.
 */
export const REASON_RULE = Object.freeze({ min: 3, max: 500, plain: true });

const reason = (body, key = 'reason') => text(body, key, REASON_RULE);

function oneOf(body, key, values) {
  const value = present(body, key);
  if (!values.includes(value)) throw invalid(key, 'must be one of ' + values.join(', '));
  return value;
}

/** A positive decimal string with at most `dp` places, returned at exactly `dp` places. */
function positiveDecimal(body, key, dp) {
  const value = present(body, key);
  const pattern = new RegExp('^\\d{1,14}(\\.\\d{1,' + dp + '})?$');
  if (typeof value !== 'string' || !pattern.test(value)) {
    throw invalid(key, 'must be a decimal string with at most ' + dp + ' decimal places');
  }
  const fixed = toFixed(value, dp);
  if (/^0+(\.0+)?$/.test(fixed)) throw invalid(key, 'must be greater than zero');
  return fixed;
}

const price = (body, key) => positiveDecimal(body, key, 4);
const money = (body, key) => positiveDecimal(body, key, 2);

/** A price, or null when absent: the exit-fee side of a fund that charges no exit fee (UTLMF). */
function optionalPrice(body, key) {
  const value = body[key];
  if (value === undefined || value === null) return null;
  return price(body, key);
}

function fundId(body, key) {
  const value = present(body, key);
  if (typeof value !== 'string' || !FUND.test(value)) throw invalid(key, 'must be a fund code such as UTIF');
  return value;
}

function roleCode(body, key) {
  const value = present(body, key);
  if (typeof value !== 'string' || !ROLE.test(value)) throw invalid(key, 'must be a role code (A-Z, 0-9, _; 2-40 characters)');
  return value;
}

/** A list of distinct values, each checked by one(value) and sorted; duplicates are refused, never merged. */
function sortedSet(body, key, one, { min, max }) {
  const value = present(body, key);
  if (!Array.isArray(value)) throw invalid(key, 'must be a list');
  if (value.length < min) throw invalid(key, 'must hold at least ' + min);
  if (value.length > max) throw invalid(key, 'must hold at most ' + max);
  const items = value.map((item) => one(item));
  const sorted = [...items].sort();
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i] === sorted[i - 1]) throw invalid(key, 'holds a duplicate: ' + sorted[i]);
  }
  return sorted;
}

function orderIds(body, key = 'orderIds') {
  return sortedSet(body, key, (id) => {
    if (typeof id !== 'string' || !UUID.test(id)) throw invalid(key, 'must hold uuids');
    return id.toLowerCase();
  }, { min: 1, max: MAX_ORDERS });
}

/** IP-127: how long a review's comment is, in characters - order_review_comment_text (0018) holds the same. */
export const REVIEW_COMMENT_LENGTH = Object.freeze({ min: 3, max: 1000 });

// The control characters a comment may NOT hold: every C0 control but the tab
// and the line breaks (\t, \n, \r), DEL, NEL, and the Unicode line and
// paragraph separators - a comment is the lines a person typed, never a
// carrier for anything else.
// eslint-disable-next-line no-control-regex
const COMMENT_CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f\u0085\u2028\u2029]/;

/**
 * IP-127 (design 2.2): ORDER_REVIEW's comment - one for every order the
 * request names, trimmed, 3 to 1,000 characters counted as PostgreSQL counts
 * them (code points: length(btrim(comment)) in order_review_comment_text), so
 * a comment the catalogue takes is one the table takes. Line breaks and tabs
 * are allowed; any other control character is refused. It never travels to
 * LIV: the review sends no event (IP-131).
 */
function reviewComment(body, key = 'comment') {
  const value = present(body, key);
  if (typeof value !== 'string') throw invalid(key, 'must be text');
  const trimmed = value.trim();
  const length = [...trimmed].length;
  if (length < REVIEW_COMMENT_LENGTH.min) throw invalid(key, 'must be at least ' + REVIEW_COMMENT_LENGTH.min + ' characters');
  if (length > REVIEW_COMMENT_LENGTH.max) throw invalid(key, 'must be at most ' + REVIEW_COMMENT_LENGTH.max + ' characters');
  if (COMMENT_CONTROL.test(trimmed)) throw invalid(key, 'must not contain control characters other than line breaks and tabs');
  return trimmed;
}

/**
 * The reasons that travel to LIV in a Core event, held to what the event's
 * schema carries (core-order-event.schema.json, core-cash-event.schema.json) -
 * the catalogue refuses what the event could not carry, or the Core would end
 * the order and publish an event that breaks its own contract:
 *
 *   checkerReason  the checker's send-back (IP-85) and reject (IP-86): 3 to 500
 *                  characters on one line - the rejected event's note and its
 *                  reasons[].message (^.+$)
 *   opsReason      an ops reject from hold (reasons[].message, ^.+$) and an ops
 *                  cancel (cancellationConfirmed's note, at most 500): one line
 *                  of at most 500, as PR #10's handler capped it
 *   returnReason   a payout's return (payoutReturned's reason, at most 500)
 *
 * All three are REASON_RULE (IP-199): 3 to 500 characters on one line, which
 * every one of those event fields can carry.
 */
const checkerReason = (body) => text(body, 'reason', REASON_RULE);
const opsReason = (body) => text(body, 'reason', REASON_RULE);
const returnReason = (body) => text(body, 'reason', REASON_RULE);

/** {orderId: state_version} for exactly the given ids, keys lower-cased. */
function expectedVersions(body, ids, key = 'expectedVersions') {
  const value = present(body, key);
  if (typeof value !== 'object' || Array.isArray(value)) throw invalid(key, 'must map each transaction id to its state_version');
  const out = {};
  for (const [id, version] of Object.entries(value)) {
    if (!UUID.test(id)) throw invalid(key, 'must be keyed by transaction ids');
    if (!Number.isSafeInteger(version) || version < 0) throw invalid(key, 'must hold non-negative whole numbers');
    const lower = id.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(out, lower)) throw invalid(key, 'holds a transaction twice: ' + lower);
    out[lower] = version;
  }
  const keys = Object.keys(out).sort();
  if (keys.length !== ids.length || keys.some((id, i) => id !== ids[i])) {
    throw invalid(key, 'must name exactly the transactions in orderIds');
  }
  return Object.fromEntries(keys.map((id) => [id, out[id]]));
}

/**
 * IP-203: ORDER_REVIEW's optional feesWaived - order id -> true to untick a
 * leg's Apply fees (deal it at the plain price, no fee), false to tick it
 * again. Fee percentages are set at fund level only, so a review sends a
 * yes/no, never a rate. Each id is one of orderIds. Left out of the body, it
 * is left out of the params, so a review without it binds exactly what it
 * always did. (IP-139's entryFeeRates is retired: refused, never ignored, so a
 * stale Console cannot think it set a rate.)
 */
function feesWaived(body, ids, key = 'feesWaived') {
  if (body.entryFeeRates !== undefined) throw invalid('entryFeeRates', 'is retired (IP-203): fees are set at fund level; send feesWaived');
  const value = body[key];
  if (value === undefined) return undefined;
  if (value === null || typeof value !== 'object' || Array.isArray(value)) throw invalid(key, 'must map transaction ids to true or false');
  const out = {};
  for (const [id, given] of Object.entries(value)) {
    if (!UUID.test(id)) throw invalid(key, 'must be keyed by transaction ids');
    const lower = id.toLowerCase();
    if (!ids.includes(lower)) throw invalid(key, 'names a transaction that is not in orderIds: ' + lower);
    if (Object.prototype.hasOwnProperty.call(out, lower)) throw invalid(key, 'holds a transaction twice: ' + lower);
    if (typeof given !== 'boolean') throw invalid(key, 'must map each transaction to true (waive the fee) or false (apply it)');
    out[lower] = given;
  }
  const keys = Object.keys(out).sort();
  if (!keys.length) throw invalid(key, 'names no transaction; leave it out instead');
  return Object.fromEntries(keys.map((id) => [id, out[id]]));
}

function wholeNumber(body, key, { min, max }) {
  const value = present(body, key);
  if (!Number.isSafeInteger(value) || value < min || value > max) throw invalid(key, 'must be a whole number from ' + min + ' to ' + max);
  return value;
}

/**
 * A fraction such as 0.015 (1.5 per cent), returned at 6 dp - numeric(8, 6)
 * and (7, 6)'s scale - and held to [min, max]; minOpen and maxOpen make an end
 * exclusive.
 */
function rate(body, key, { min, max, minOpen = false, maxOpen = false }) {
  const value = present(body, key);
  if (typeof value !== 'string' || !/^\d(\.\d{1,6})?$/.test(value)) {
    throw invalid(key, 'must be a rate such as 0.015 (1.5 per cent), with at most 6 decimal places');
  }
  const fixed = toFixed(value, 6);
  const low = cmp(fixed, min);
  const high = cmp(fixed, max);
  if ((minOpen ? low <= 0 : low < 0) || (maxOpen ? high >= 0 : high > 0)) {
    throw invalid(key, 'must be ' + (minOpen ? 'above ' : 'at least ') + min + ' and ' + (maxOpen ? 'below ' : 'at most ') + max);
  }
  return fixed;
}

/**
 * FUND_FEES_PROPOSE (IP-98..IP-101, IP-136): a fund's fees from an effective
 * date, and why. Fees are percentages only (IP-136): the exit fee is NONE (no
 * value) or PERCENT (a rate above 0, at most 0.10); its holding period is 1
 * to 120 calendar months (a number, as JSON carries it), named even for NONE
 * so the field is never ambiguous. The entry fee is PERCENT (a rate, 0 up to
 * but not 1; 0 is no fee). IP-99's PER_UNIT and IP-100's FIXED are refused
 * here, as any other unknown basis is (VALIDATION), and migration 0020 holds
 * an open proposal to the same.
 *
 * The shape is unchanged since 0011 - entryFeeBasis is still named, and
 * always PERCENT - so the params of a percentage proposed before IP-136, and
 * the digest its approval re-derives from the stored row (fees/rules.js
 * paramsOfProposal), are the ones its proposer's code was bound to. Whether
 * these fees suit the fund and the date needs the fund, so fees/rules.js
 * decides it.
 *
 * IP-179: transactionChargeRate, the fund's transaction charge rate from the
 * effective date, from 0 to 0.05 (fund_config's range) - required of every
 * proposal from now on; the Console pre-fills the rate in force. A proposal
 * stored before it has none, and paramsOfProposal leaves the key out for it,
 * so its digest is still the one its proposer's code was bound to.
 *
 * IP-204: feeDefaults, the fund's Apply fees conditions (feeDefaultConditions
 * below) - optional, and left out of the params when absent, for the same
 * reason.
 */
function feeProposal(b) {
  const exitFeeBasis = oneOf(b, 'exitFeeBasis', ['NONE', 'PERCENT']);
  let exitFeeValue = null;
  if (exitFeeBasis === 'PERCENT') exitFeeValue = rate(b, 'exitFeeValue', { min: '0', max: '0.10', minOpen: true });
  else if (b.exitFeeValue !== undefined && b.exitFeeValue !== null) throw invalid('exitFeeValue', 'must be absent when exitFeeBasis is NONE');
  const params = {
    fundId: fundId(b, 'fundId'),
    effectiveDate: date(b, 'effectiveDate'),
    exitFeeBasis,
    exitFeeValue,
    exitFeeHoldingMonths: wholeNumber(b, 'exitFeeHoldingMonths', { min: 1, max: 120 }),
    entryFeeBasis: oneOf(b, 'entryFeeBasis', ['PERCENT']),
    entryFeeValue: rate(b, 'entryFeeValue', { min: '0', max: '1', maxOpen: true }),
    transactionChargeRate: rate(b, 'transactionChargeRate', { min: '0', max: '0.05' }),
    reason: text(b, 'reason', { min: 3, max: 500, plain: true }),
  };
  // IP-204: checked last, against the fees above; left out of the params when the body leaves it out, so such
  // a proposal binds what it always did.
  const feeDefaults = feeDefaultConditions(b, params);
  return feeDefaults ? { ...params, feeDefaults } : params;
}

/**
 * IP-204: FUND_FEES_PROPOSE's optional feeDefaults - the fund's two Apply
 * fees conditions from the effective date:
 *
 *   { entryAbove: '100000.00' | null, exitAbove: '450000.00' | null }
 *
 * An amount (LKR, a decimal string, above 0, at most 2 places) switches the
 * condition on: a leg's box starts ticked only when its value is MORE THAN
 * it. null switches it off: the box always starts ticked. Both keys are
 * required inside the object, so "off" is never confused with "not said".
 * A condition on a fee the proposal does not charge is refused (a front-end
 * rate of 0, an exit fee of NONE): there would be no box to tick.
 *
 * Absent altogether: the proposal keeps the conditions in force (an approval
 * copies the open row's), exactly as every proposal before IP-204 did.
 */
function feeDefaultConditions(body, { exitFeeBasis, entryFeeValue }) {
  const value = body.feeDefaults;
  if (value === undefined) return undefined;
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw invalid('feeDefaults', 'must be { entryAbove, exitAbove }, each an LKR amount or null');
  }
  const extra = Object.keys(value).filter((k) => k !== 'entryAbove' && k !== 'exitAbove');
  if (extra.length) throw invalid('feeDefaults', 'names an unknown condition: ' + extra.join(', '));
  const one = (key) => {
    if (!Object.prototype.hasOwnProperty.call(value, key)) throw invalid('feeDefaults.' + key, 'is required: an LKR amount, or null for off');
    // Named in full in a refusal, so the Console can put it beside the right input.
    return value[key] === null ? null : money({ ['feeDefaults.' + key]: value[key] }, 'feeDefaults.' + key);
  };
  const entryAbove = one('entryAbove');
  const exitAbove = one('exitAbove');
  if (entryAbove !== null && cmp(entryFeeValue, '0') === 0) {
    throw invalid('feeDefaults.entryAbove', 'must be null: the front-end fee is 0, so there is no Apply fees box to tick');
  }
  if (exitAbove !== null && exitFeeBasis === 'NONE') {
    throw invalid('feeDefaults.exitAbove', 'must be null: the fund charges no exit fee, so there is no Apply fees box to tick');
  }
  return { entryAbove, exitAbove };
}

/** A sorted set of role codes. */
function roleSet(body, key, { min }) {
  return sortedSet(body, key, (role) => {
    if (typeof role !== 'string' || !ROLE.test(role)) throw invalid(key, 'must hold role codes');
    return role;
  }, { min, max: 20 });
}

/**
 * An uploaded file's name (C-32, spec 37.7.1/37.7.5; ST-4.5): a name, not a
 * path, ending .csv - the one type both upload classes take - with something
 * before it. The Console checked the extension; the server did not, so an
 * uploader calling the API could store, say, statement.iqy, and a checker's
 * download saved it under that name. It is kept verbatim for display
 * (intake_batch.file_name); the download is saved under batch.js
 * downloadName, never this.
 */
function fileName(body, key) {
  const value = text(body, key, { max: 255, plain: true });
  if (/[\\/]/.test(value) || value === '.' || value === '..') throw invalid(key, 'must be a file name, not a path');
  if (!/\.csv$/i.test(value) || !/[^.\s]/.test(value.slice(0, -4))) throw invalid(key, 'must be a CSV file name, ending .csv');
  return value;
}

const PERSON_NAME = /^[\p{L}][\p{L}\p{M} '-]{0,49}$/u;

/** A first or last name: 1-50 characters, letters, spaces, apostrophes and hyphens, starting with a letter. */
export function personName(body, key) {
  const value = text(body, key, { max: 50, plain: true });
  if (!PERSON_NAME.test(value)) throw invalid(key, 'must be a name: letters, spaces, apostrophes and hyphens');
  return value;
}

function email(body, key) {
  const value = text(body, key, { max: 254, plain: true }).toLowerCase();
  if (!EMAIL.test(value)) throw invalid(key, 'must be an email address');
  return value;
}

/** One file's facts, the six an upload's code is bound to (and its kind - IP-103). */
function uploadFile(b) {
  const fileClass = oneOf(b, 'fileClass', FILE_CLASSES);
  // A Core data file is INITIAL or DAILY (IP-103), and so is a WM file (0026); a NAV file and a statement have no kind.
  let loadKind = null;
  if (takesLoadKind(fileClass)) loadKind = oneOf(b, 'loadKind', LOAD_KINDS);
  else if (b.loadKind !== undefined && b.loadKind !== null) throw invalid('loadKind', 'must be absent for a ' + fileClass + ' file');
  // A statement names the collection account it was downloaded from; no other file does.
  let accountId = null;
  if (fileClass === 'BANK_STATEMENT') accountId = uuid(b, 'accountId');
  else if (b.accountId !== undefined && b.accountId !== null) throw invalid('accountId', 'must be absent for a ' + fileClass + ' file');
  return {
    fileClass,
    loadKind,
    businessDate: date(b, 'businessDate'),
    accountId,
    fileName: fileName(b, 'fileName'),
    sha256: hex64(b, 'sha256'),
    contentLength: wholeNumber(b, 'contentLength', { min: 1, max: MAX_UPLOAD_BYTES }),
  };
}

/** Files one INTAKE_UPLOAD_BATCH code covers. */
export const MAX_UPLOAD_FILES = 10;

/**
 * INTAKE_UPLOAD_BATCH's files: 2 to MAX_UPLOAD_FILES, each uploadFile's, in
 * the order given (the ticket answers in that order). A field is named with
 * its place - files[1].sha256 - and the same bytes or name twice is refused.
 */
function uploadFiles(body, key) {
  const value = present(body, key);
  if (!Array.isArray(value)) throw invalid(key, 'must be a list of files');
  if (value.length < 2 || value.length > MAX_UPLOAD_FILES) throw invalid(key, 'must hold 2 to ' + MAX_UPLOAD_FILES + ' files');
  const files = value.map((item, i) => {
    const at = key + '[' + i + ']';
    if (item === null || typeof item !== 'object' || Array.isArray(item)) throw invalid(at, 'must be a file\'s facts');
    try {
      return uploadFile(item);
    } catch (error) {
      if (!(error instanceof DomainError) || !error.details?.field) throw error;
      throw invalid(at + '.' + error.details.field, error.message.slice(error.details.field.length + 2));
    }
  });
  for (const field of ['sha256', 'fileName']) {
    const seen = new Set();
    files.forEach((f, i) => {
      const same = field === 'fileName' ? f.fileName.toLowerCase() : f.sha256;
      if (seen.has(same)) throw invalid(key + '[' + i + '].' + field, 'is the same as an earlier file\'s');
      seen.add(same);
    });
  }
  return files;
}

/**
 * ROLE_SAVE's notifications (docs/notifications/implementation-guide.md 8.1):
 * the notification codes the role subscribes to, edited in the same dialog as
 * its claims.
 *
 *   absent or null  -> null: KEEP the role's current subscriptions. Old
 *                      clients that know nothing of notifications still work;
 *                      authority/access.js saveRole then drops only those the
 *                      new claim set no longer covers.
 *   a list          -> the WHOLE new set, sorted, each code checked here so a
 *                      bad set is refused (400) before anything is written:
 *                        - a code this build knows;
 *                        - not a SUBJECT-only code (it has no subscribers);
 *                        - covered by THIS request's claims: the role must hold
 *                          one of the code's required claims (rule 3) - the
 *                          dialog unticks a notification when the claim that
 *                          covered it is unticked, and this is the server's
 *                          copy of that rule (0024's trigger is the third).
 */
function roleNotifications(body, claims) {
  if (body.notifications === undefined || body.notifications === null) return null;
  return sortedSet(body, 'notifications', (code) => {
    if (!isNotification(code)) throw invalid('notifications', 'holds an unknown notification: ' + String(code));
    if (notificationOf(code).audience === 'SUBJECT') {
      throw invalid('notifications', code + ' is sent to the person it is about; no role subscribes to it');
    }
    if (!coveredBy(code, claims)) {
      throw invalid('notifications', code + ' needs one of ' + notificationOf(code).requiredClaims.join(', ') + ', which this role does not hold');
    }
    return code;
  }, { min: 0, max: 64 });
}

// ---------------------------------------------------------------------------
// The catalogue
// ---------------------------------------------------------------------------

function objectBody(body) {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) {
    throw new DomainError('VALIDATION', 'the request body must be a JSON object', { status: 400 });
  }
  return body;
}

/** An entry: the normaliser is wrapped so every one refuses a non-object body the same way. */
function entry(actionClass, claims, normalise, { group = 'new', entityKey, leg = 1, selfChangeKey, stepUp = false } = {}) {
  const e = {
    actionClass,
    claims: Object.freeze([...claims]),
    params: (body) => normalise(objectBody(body)),
    group,
    leg,
    stepUp,
  };
  if (entityKey) e.entityKey = entityKey;
  if (selfChangeKey) e.selfChangeKey = selfChangeKey;
  return Object.freeze(e);
}

const ACCESS = ['core.access.admin'];

export const ACTION = Object.freeze({
  // --- File uploads (IP-44, IP-60; IP-91, IP-96; IP-103) ---------------------
  // All three need a fresh code. The upload's is bound to the file's facts -
  // its class and kind, date, account, name, digest and length (the bytes go
  // to S3 on the presigned POST its ticket returns); accept's and reject's to
  // the batch and the bytes the checker was shown.
  INTAKE_UPLOAD: entry('L1', ['core.intake.upload'], uploadFile, { group: 'entity', entityKey: 'batchId', leg: 1, stepUp: true }),
  // Several files on one code: each file's facts exactly as INTAKE_UPLOAD's,
  // in the order given, at most MAX_UPLOAD_FILES and no two the same bytes or
  // name. One evidence row (leg 1 of a fresh group) stands behind every batch
  // the ticket opens; each batch's own CHECK holds its acceptor to another
  // person (intake_batch_uploader_is_not_acceptor).
  INTAKE_UPLOAD_BATCH: entry('L1', ['core.intake.upload'], (b) => ({ files: uploadFiles(b, 'files') }), { stepUp: true }),
  INTAKE_ACCEPT: entry('L2', ['core.intake.accept'], (b) => ({
    batchId: uuid(b, 'batchId'), sha256: hex64(b, 'sha256'), summarySha256: hex64(b, 'summarySha256'),
  }), { group: 'entity', entityKey: 'batchId', leg: 2, stepUp: true }),
  INTAKE_REJECT: entry('L1', ['core.intake.accept'], (b) => ({ batchId: uuid(b, 'batchId'), sha256: hex64(b, 'sha256'), reason: reason(b) }),
    { stepUp: true }),

  // --- Unit prices (IP-61) - each a decision on a fresh code (IP-111, D-D) ---
  PRICE_OVERRIDE: entry('L1', ['core.price.override'], (b) => {
    const navRunId = uuid(b, 'navRunId');
    const checkCode = present(b, 'checkCode');
    if (typeof checkCode !== 'string' || !CODE_NAME.test(checkCode)) throw invalid('checkCode', 'must be a check code such as DAY_ON_DAY');
    // nav_run_override_reason_length: 20 characters or more.
    // Its own, stricter rule, unchanged by IP-199: 20 to 1,000 characters
    // (migration nav_run_override_reason_length; the Console's OVERRIDE_REASON_MIN).
    return { navRunId, checkCode, reason: text(b, 'reason', { min: 20, max: 1000 }) };
  }, { stepUp: true }),
  // Migration 0016: the approval publishes the price, so the code is bound to
  // every figure published - the exit-fee price included, null for a fund
  // with no exit fee (nav_run.buying_price_with_exit_fee is NULL there), and
  // the selling price with the front-end fee (IP-179), null on a run struck
  // before 0034.
  PRICE_APPROVE: entry('L2', ['core.price.approve'], (b) => ({
    navRunId: uuid(b, 'navRunId'),
    navPerUnit: price(b, 'navPerUnit'),
    sellingPrice: price(b, 'sellingPrice'),
    buyingPrice: price(b, 'buyingPrice'),
    buyingPriceWithExitFee: optionalPrice(b, 'buyingPriceWithExitFee'),
    sellingPriceWithEntryFee: optionalPrice(b, 'sellingPriceWithEntryFee'),
  }), { group: 'entity', entityKey: 'navRunId', leg: 2, stepUp: true }),
  PRICE_REJECT: entry('L1', ['core.price.approve'], (b) => ({ navRunId: uuid(b, 'navRunId'), reason: reason(b) }),
    { stepUp: true }),

  // --- Cash (IP-57..59, IP-67..71) - each decision on a fresh code (IP-111) ---
  MATCH_CLAIM: entry('L1', ['core.cash.claim'], (b) => ({ lineId: hex64(b, 'lineId'), movementId: uuid(b, 'movementId') }),
    { group: 'entity', entityKey: 'matchId', leg: 1, stepUp: true }),
  MATCH_APPROVE: entry('L2', ['core.cash.approve'], (b) => ({ matchId: uuid(b, 'matchId'), amount: money(b, 'amount') }),
    { group: 'entity', entityKey: 'matchId', leg: 2, stepUp: true }),
  // Spec 6.1: "leg 2; != claimant" for approve and reject alike.
  MATCH_REJECT: entry('L1', ['core.cash.approve'], (b) => ({ matchId: uuid(b, 'matchId'), reason: reason(b) }),
    { group: 'entity', entityKey: 'matchId', leg: 2, stepUp: true }),
  LINE_CLEAR: entry('L1', ['core.cash.claim'], (b) => ({
    lineId: hex64(b, 'lineId'),
    clearedOn: date(b, 'clearedOn'),
    bankReference: text(b, 'bankReference', { max: 100, plain: true }),
    reason: reason(b),
  }), { stepUp: true }),
  LINE_RETURN: entry('L1', ['core.cash.claim'], (b) => ({ lineId: hex64(b, 'lineId'), returnedOn: date(b, 'returnedOn'), reason: reason(b) }),
    { stepUp: true }),
  // IP-138: an automatic match undone, with the comment that says why - a
  // decision on a match, so the checker's (as MATCH_REJECT), on a fresh code.
  LINE_UNRECONCILE: entry('L1', ['core.cash.approve'], (b) => ({ lineId: hex64(b, 'lineId'), reason: reason(b) }),
    { stepUp: true }),
  // IP-157: a left-over line cleared by hand, with the reason that says why -
  // it takes a line out of the reconciliation, so the checker's, on a fresh code.
  LINE_MANUAL_CLEAR: entry('L1', ['core.cash.approve'], (b) => ({ lineId: hex64(b, 'lineId'), reason: reason(b) }),
    { stepUp: true }),
  RECEIPT_REFUND: entry('L1', ['core.payout.manage'], (b) => ({
    lineId: hex64(b, 'lineId'), refundReference: text(b, 'refundReference', { max: 100, plain: true }), reason: reason(b),
  }), { stepUp: true }),
  // IP-112: a note on a statement line, by whoever claims or approves its
  // matches - one line of 1 to 500 characters, on the session (a note is not a
  // decision), a fresh group of its own.
  LINE_NOTE: entry('L1', ['core.cash.claim', 'core.cash.approve'], (b) => ({
    lineId: hex64(b, 'lineId'), note: text(b, 'note', { max: 500, plain: true }),
  })),

  // --- Books and orders (IP-56, IP-62, PR #10) -------------------------------
  BOOK_CLOSE: entry('L1', ['core.book.close'], (b) => ({ fundId: fundId(b, 'fundId'), dealingDate: date(b, 'dealingDate'), reason: reason(b) })),
  // IP-135: a person moves a business day on (business-day/action.js), as the
  // book close's fallback is a person's: core.book.close, on the session, a
  // fresh group. force (absent is false) lets the day past a price still
  // missing, or statements and the funding window - never past pending
  // orders - and then a reason is required: 3 to 500 characters on one line,
  // kept on the day's forced list and its event. Without force a reason is
  // optional; it is kept on the evidence's audit row.
  DAY_ADVANCE: entry('L1', ['core.book.close'], (b) => {
    const force = optionalFlag(b, 'force');
    const options = { min: 3, max: 500, plain: true };
    return { businessDate: date(b, 'businessDate'), force, reason: force ? text(b, 'reason', options) : optionalText(b, 'reason', options) };
  }),
  // IP-195 (owner, 17 Oct 2026): a person runs a business day's Day end from the
  // Console - "a button to execute the day end process with approved transactions".
  // The Day end machine starts for that day now: the day's priced transactions
  // nobody approved are carried over when reconciled and rolled to the next
  // dealing date when not (day-end/carry.js), then every holding is checked and
  // the day closed. core.book.close, as DAY_ADVANCE, but with an authenticator
  // code: it moves transactions on. A reason is optional, kept on the audit row.
  DAY_END_RUN: entry('L1', ['core.book.close'], (b) => ({
    businessDate: date(b, 'businessDate'), reason: optionalText(b, 'reason', { min: 3, max: 500, plain: true }),
  }), { stepUp: true }),
  // IP-127 (design 2.2): the maker's review - one comment for every order it
  // names, and the state_version each showed (as ORDER_APPROVE's): a version
  // that moved is skipped, APR-012. No fee: IP-136 took the review's fee edit
  // away, and the normaliser drops a fees field an old Console still sends.
  // IP-203: and, optionally, whether each leg's fee is waived (feesWaived).
  ORDER_REVIEW: entry('L1', ['core.order.review'], (b) => {
    const ids = orderIds(b);
    const waived = feesWaived(b, ids);
    return { orderIds: ids, expectedVersions: expectedVersions(b, ids), comment: reviewComment(b), ...(waived ? { feesWaived: waived } : {}) };
  }, { leg: 1 }),
  ORDER_APPROVE: entry('L2', ['core.order.approve'], (b) => {
    const ids = orderIds(b);
    return { orderIds: ids, expectedVersions: expectedVersions(b, ids) };
  }, { leg: 2, stepUp: true }), // IP-199: an authenticator code per approval request
  // IP-85, IP-86: the checker, instead of approving - back to the maker for a new
  // review (a reviewed order: its review cycle ends, IP-127), or rejected (an
  // order in any open state, IP-130: its pair partner, and a parked order's
  // whole instruction, with it). One evidence row each: nothing pairs with them.
  ORDER_SEND_BACK: entry('L1', ['core.order.approve'], (b) => ({ orderIds: orderIds(b), reason: checkerReason(b) })),
  // IP-199: rejecting a transaction - from any open state, or from hold - needs an authenticator code.
  ORDER_REJECT: entry('L1', ['core.order.approve'], (b) => ({ orderIds: orderIds(b), reason: checkerReason(b) }), { stepUp: true }),
  ORDER_RELEASE: entry('L1', ['core.order.manage'], (b) => ({ orderId: uuid(b, 'orderId') })),
  ORDER_REJECT_HOLD: entry('L1', ['core.order.manage'], (b) => ({ orderId: uuid(b, 'orderId'), reason: opsReason(b) }), { stepUp: true }),
  ORDER_CANCEL: entry('L1', ['core.order.manage'], (b) => ({ orderId: uuid(b, 'orderId'), reason: opsReason(b) })),

  // --- Payouts (IP-63) ---------------------------------------------------------
  PAYOUT_GENERATE: entry('L1', ['core.payout.manage'], (b) => ({
    fundId: fundId(b, 'fundId'), businessDate: date(b, 'businessDate'), kind: oneOf(b, 'kind', ['FULL', 'FAILED_ONLY']),
  })),
  PAYOUT_SUBMIT: entry('L1', ['core.payout.manage'], (b) => ({
    payoutBatchId: uuid(b, 'payoutBatchId'), sha256: hex64(b, 'sha256'), bankReference: text(b, 'bankReference', { max: 255, plain: true }),
  })),
  PAYOUT_RETURN: entry('L1', ['core.payout.manage'], (b) => ({ itemId: uuid(b, 'itemId'), reason: returnReason(b) })),
  PAYOUT_CONFIRM: entry('L1', ['core.payout.manage'], (b) => ({ itemId: uuid(b, 'itemId'), note: optionalText(b, 'note', { max: 1000 }) })),

  // --- Holders (IP-51) ---------------------------------------------------------
  HOLDER_PROPOSE: entry('L1', ['core.holder.propose'], (b) => ({
    livAccountId: uuid(b, 'livAccountId'),
    livStrategyId: uuid(b, 'livStrategyId'),
    accountActivationCode: text(b, 'accountActivationCode', { max: 20, plain: true }),
    strategyRef: text(b, 'strategyRef', { max: 40, plain: true }),
    // Only the types next_holder_no can number (IN: INDIVIDUAL, JOINT; CP:
    // CORPORATE, TRUST). An ESTATE or NOMINEE proposal could never be approved.
    holderType: oneOf(b, 'holderType', ['INDIVIDUAL', 'CORPORATE', 'JOINT', 'TRUST']),
    fullName: text(b, 'fullName', { max: 255, plain: true }),
  }), { group: 'entity', entityKey: 'requestId', leg: 1 }),
  HOLDER_APPROVE: entry('L1', ['core.holder.approve'], (b) => ({ requestId: uuid(b, 'requestId') }),
    { group: 'entity', entityKey: 'requestId', leg: 2 }),
  // Ruling R16 (Task 8 review): a checker declines a proposal, or its proposer
  // withdraws it - no second person, so a fresh approval group, leg 1.
  HOLDER_REJECT: entry('L1', ['core.holder.approve'], (b) => ({
    requestId: uuid(b, 'requestId'), reason: text(b, 'reason', REASON_RULE),
  })),
  HOLDER_STATUS: entry('L1', ['core.holder.propose'], (b) => ({
    holderId: uuid(b, 'holderId'), status: oneOf(b, 'status', ['ACTIVE', 'DORMANT', 'CLOSED']), reason: reason(b),
  })),

  // --- Fund fees (IP-98..IP-102): four eyes, a fresh code for every leg -------
  // The proposer's code is bound to the fees themselves; the approver's to the
  // proposal and the proposer's digest of them (payloadSha256); a rejection's
  // to the proposal and its reason. Approve and reject are both leg 2 of the
  // proposal's group, so the proposer can do neither (unique (approval_group, uid)).
  FUND_FEES_PROPOSE: entry('L1', ['core.fees.propose'], feeProposal,
    { group: 'entity', entityKey: 'proposalId', leg: 1, stepUp: true }),
  FUND_FEES_APPROVE: entry('L2', ['core.fees.approve'], (b) => ({
    proposalId: uuid(b, 'proposalId'), payloadSha256: hex64(b, 'payloadSha256'),
  }), { group: 'entity', entityKey: 'proposalId', leg: 2, stepUp: true }),
  FUND_FEES_REJECT: entry('L1', ['core.fees.approve'], (b) => ({ proposalId: uuid(b, 'proposalId'), reason: checkerReason(b) }),
    { group: 'entity', entityKey: 'proposalId', leg: 2, stepUp: true }),

  // --- Day-end analytics feeds (IP-142..IP-156): one person, a fresh code, a reason ------
  FEEDS_RESEND: entry('L1', ['core.feeds.send'], (b) => ({ businessDate: date(b, 'businessDate'), reason: checkerReason(b) }), { stepUp: true }),
  FEEDS_WM_WAIVE: entry('L1', ['core.feeds.send'], (b) => ({ businessDate: date(b, 'businessDate'), reason: checkerReason(b) }), { stepUp: true }),

  // --- Access (IP-50, IP-78): an admin, alone - never on themself ------------
  USER_INVITE: entry('L1', ACCESS, (b) => ({
    email: email(b, 'email'),
    firstName: personName(b, 'firstName'),
    lastName: personName(b, 'lastName'),
    roles: roleSet(b, 'roles', { min: 1 }),
  }), { selfChangeKey: 'email' }),
  USER_RESEND_INVITE: entry('L1', ACCESS, (b) => ({ uid: uuid(b, 'uid') }), { selfChangeKey: 'uid' }),
  USER_SUSPEND: entry('L1', ACCESS, (b) => ({ uid: uuid(b, 'uid'), reason: reason(b) }), { selfChangeKey: 'uid' }),
  USER_REINSTATE: entry('L1', ACCESS, (b) => ({ uid: uuid(b, 'uid'), reason: reason(b) }), { selfChangeKey: 'uid' }),
  // The whole set, replacing what the user held: one change the admin reads as one.
  USER_ROLES_SET: entry('L1', ACCESS, (b) => ({ uid: uuid(b, 'uid'), roles: roleSet(b, 'roles', { min: 0 }) }), { selfChangeKey: 'uid' }),
  ROLE_SAVE: entry('L1', ACCESS, (b) => {
    const claims = sortedSet(b, 'claims', (code) => {
      if (!isClaim(code)) throw invalid('claims', 'holds an unknown claim: ' + String(code));
      return code;
    }, { min: 0, max: 64 });
    return {
      role: roleCode(b, 'role'),
      name: text(b, 'name', { max: 80, plain: true }),
      description: optionalText(b, 'description', { max: 1000 }),
      claims,
      notifications: roleNotifications(b, claims),
    };
  }, { selfChangeKey: 'role' }),
  ROLE_RETIRE: entry('L1', ACCESS, (b) => ({ role: roleCode(b, 'role') }), { selfChangeKey: 'role' }),
  ROLE_ASSIGN: entry('L1', ACCESS, (b) => ({ uid: uuid(b, 'uid'), role: roleCode(b, 'role') }), { selfChangeKey: 'uid' }),
  ROLE_REMOVE: entry('L1', ACCESS, (b) => ({ uid: uuid(b, 'uid'), role: roleCode(b, 'role') }), { selfChangeKey: 'uid' }),
  // IP-78: the person's authenticator is revoked in the Core and disabled in
  // Cognito; they set up a new one at their next sign-in.
  MFA_RESET: entry('L1', ACCESS, (b) => ({ uid: uuid(b, 'uid'), reason: reason(b) }), { selfChangeKey: 'uid' }),
  // docs/notifications/implementation-guide.md 8.1 (migration 0025): the three
  // settings an administrator may change for one notification code - whether
  // it is sent at all, whether it is also emailed, and the email cooldown.
  // Everything else about a code is fixed in notifications/catalogue.js. A
  // locked security notice's email cannot be switched off: the normaliser
  // refuses it (and switching such a code off) here, authority/access.js
  // again, and 0024's CHECK last. Not a
  // self-change action: a code is nobody's own account.
  NOTIFICATION_CONFIG_SAVE: entry('L1', ACCESS, (b) => {
    const code = present(b, 'code');
    if (!isNotification(code)) throw invalid('code', 'is not a notification: ' + String(code));
    // A locked security notice (MFA reset, password changed, step-up locked)
    // is always sent and always emailed: switching it off, or its email, would
    // silence exactly the notice a person needs to say "that was not me".
    const enabled = flag(b, 'enabled');
    const forceEmail = flag(b, 'forceEmail');
    if (notificationOf(code).emailLocked && (!enabled || !forceEmail)) {
      throw invalid(enabled ? 'forceEmail' : 'enabled', code + ' is a security notice and is always sent by email');
    }
    return {
      code,
      enabled,
      forceEmail,
      emailCooldownMinutes: wholeNumber(b, 'emailCooldownMinutes', { min: 0, max: 1440 }),
    };
  }),
});

/** The catalogue entry for a type, or VALIDATION (400) for a type the catalogue does not know. */
export function actionOf(actionType) {
  if (typeof actionType !== 'string' || !Object.prototype.hasOwnProperty.call(ACTION, actionType)) {
    throw new DomainError('VALIDATION', 'unknown action type: ' + String(actionType), { status: 400, details: { field: 'actionType' } });
  }
  return ACTION[actionType];
}

/**
 * Refuse (CLAIM_REQUIRED, 403) unless the actor holds one of the claims that
 * grant the action. The claims come from the authorizer's context, never from
 * the request body.
 */
export function requireClaim(actor, actionType) {
  const { claims } = actionOf(actionType);
  const held = Array.isArray(actor?.claims) ? actor.claims : [];
  if (!claims.some((claim) => held.includes(claim))) {
    throw new DomainError('CLAIM_REQUIRED', 'this action needs the claim ' + claims.join(' or '), {
      status: 403,
      details: { actionType, claims: [...claims] },
    });
  }
}
